import {Component, ElementRef, EventEmitter, forwardRef, Input, NgZone, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

const MONACO_BASE_PATH = 'https://cdn.jsdelivr.net/npm/monaco-editor/min/vs';

const WINDOW = (<any>window);

@Component({
  selector: 'monaco-editor',
  template: '',
  styleUrls: ['./monaco-editor-wrapper.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MonacoEditorComponent),
    multi: true
  }]
})
export class MonacoEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Output() textChanged = new EventEmitter();
  @Output() textChange = new EventEmitter();
  @Input() style: any = {};
  @Input() isDocumentEditor: boolean = false;
  private _options: any = {};
  private _readOnly = false;
  private _theme = 'monokai';
  private _mode = 'html';
  private _autoUpdateContent = true;
  private _editor?: any;
  private _durationBeforeCallback = 0;
  private _text = '';

  oldText: any;
  timeoutSaving: any;
  triggerChange = false;
  
  private _basePath?: string;

  constructor(private elementRef: ElementRef, private zone: NgZone) {}

  ngOnInit() {
    const el = this.elementRef.nativeElement;
    this.setBasePath(this._basePath ?? MONACO_BASE_PATH);

    WINDOW.MonacoEnvironment = {
      getWorkerUrl: function (workerId, label) {
        return `data:text/javascript;charset=utf-8,${encodeURIComponent(`
           self.MonacoEnvironment = {
             baseUrl: 'https://cdn.jsdelivr.net/npm/monaco-editor/min/'
           };
           importScripts('https://cdn.jsdelivr.net/npm/monaco-editor/min/vs/base/worker/workerMain.js');`)}`;
      }
    };

    WINDOW.require(["vs/editor/editor.main"], () => {
      if (this.isDocumentEditor) {
        const offset = el.parentElement.getBoundingClientRect();
        const top = offset.top;
        const height = window.innerHeight - top - 20;

        this._editor = WINDOW.monaco.editor.create(el, {
          value: this._text,
          minimap: { enabled: false },
          automaticLayout: true,
          dimension: { height: height },
          contextmenu: false,
          fontSize: 12,
          scrollbar: {
            useShadows: false,
            vertical: "visible",
            horizontal: "visible",
            horizontalScrollbarSize: 12,
            verticalScrollbarSize: 12
          },
          ...(this._options || {})
        });
      } else {
        this._editor = WINDOW.monaco.editor.create(el, {
          value: this._text,
          minimap: { enabled: false },
          automaticLayout: true,
          contextmenu: false,
          fontSize: 12,
          scrollbar: {
            useShadows: false,
            vertical: "visible",
            horizontal: "visible",
            horizontalScrollbarSize: 12,
            verticalScrollbarSize: 12
          },
          ...(this._options || {})
        });
      }

      if (this._options?.viMode) {
        this._editor.addAction({
          // an unique identifier of the contributed action
          id: "monaco-vim",
          // a label of the action that will be presented to the user
          label: "MONACO VIM!",
          keybindings: [WINDOW.monaco.KeyMod.CtrlCmd | WINDOW.monaco.KeyCode.KEY_S],
    
          // the method that will be executed when the action is triggered.
          run: (editor) => {
            this._options.viOnWrite();
            return null;
          }
        });
    
        WINDOW.require.config({
          paths: {
            "monaco-vim": "https://unpkg.com/monaco-vim/dist/monaco-vim"
          }
        });
    
        WINDOW.require(["monaco-vim"], (MonacoVim) => {
          const statusNode = WINDOW.document.getElementById('monacoVimStatus_' + this._options.fieldName);
          MonacoVim.initVimMode(this._editor, statusNode);
        });
      }


      // this._editor.setOptions(this._options ?? {});
      this.initEvents();
    });
  }

  ngOnDestroy() {
    this._editor.getModel().dispose();
    window.location.reload();
  }

  initEvents() {
    this._editor.onKeyDown((event) => {
      this.triggerChange = true;
    });

    this._editor.onDidChangeModelContent((event) => {
      this.updateText();
    });

    this._editor.onDidPaste(() => {
      this.triggerChange = true;
      this.updateText();
    });


    // this._editor.on('change', () => this.updateText());
    // this._editor.on('paste', () => this.updateText());
  }

  updateText() {
    const newVal = this._editor.getValue();
    if (newVal === this.oldText) {
      return;
    }
    if (!this._durationBeforeCallback) {
      this._text = newVal;
      if (this.triggerChange) {
        this.zone.run(() => {
          this.textChange.emit(newVal);
          this.textChanged.emit(newVal);
        });
        this._onChange(newVal);
      }
    } else {
      if (this.timeoutSaving) {
        clearTimeout(this.timeoutSaving);
      }

      this.timeoutSaving = setTimeout(() => {
        this._text = newVal;
        if (this.triggerChange) {
          this.zone.run(() => {
            this.textChange.emit(newVal);
            this.textChanged.emit(newVal);
          });
        }
        this.timeoutSaving = null;
      }, this._durationBeforeCallback);
    }
    this.oldText = newVal;
  }

  @Input() set options(options: any) {
    this.setOptions(options);
  }

  setOptions(options: { [p: string]: any }) {
    this._options = options;
    // this._editor?.setOptions(options || {});
  }

  @Input() set readOnly(readOnly: boolean) {
    this.setReadOnly(readOnly);
  }

  setReadOnly(readOnly: boolean) {
    this._readOnly = readOnly;
  }

  get value() {
    return this.text;
  }

  @Input()
  set value(value: string) {
    this.setText(value);
  }

  writeValue(value: any) {
    this.setText(value);
  }

  private _onChange = (_: any) => {
  };

  registerOnChange(fn: any) {
    this._onChange = fn;
  }

  private _onTouched = () => {
  };

  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  get text() {
    return this._text;
  }

  @Input()
  set text(text: string) {
    this.setText(text);
  }

  setText(text: any) {
    if (text === null || text === undefined) {
      text = '';
    }
    if (this._text !== text && this._autoUpdateContent === true) {
      this._text = text;
      this._editor?.setValue(text);
    }
  }

  @Input() set autoUpdateContent(status: any) {
    this.setAutoUpdateContent(status);
  }

  setAutoUpdateContent(status: any) {
    this._autoUpdateContent = status;
  }

  @Input() set durationBeforeCallback(num: number) {
    this.setDurationBeforeCallback(num);
  }

  setDurationBeforeCallback(num: number) {
    this._durationBeforeCallback = num;
  }


  @Input() set basePath(path: string) {
    this.setBasePath(path)
  }

  setBasePath(path: string) {
    this._basePath = path;

    if (!this._editor) {
      WINDOW.require.config({
        paths: { vs: path }
      });
    }
  }

  getEditor(): any {
    return this._editor
  }

}
